/**
 * VIBE_CODING_STANDARD
 *
 * -----
 * VERSION_HISTORY:
 * 1.0.0:gemini-1.5-flash (PREV) - Initial application of VIBE_CODING_STANDARD directives.
 *                                   - Added CSV export functionality.
 *                                   - Ensured `totalItems` prop is passed to Pagination.
 *                                   - Integrated Spinner for loading states.
 *                                   - Added `TieredCostViewer` for displaying complex cost details.
 * 1.1.0:gemini-1.5-flash (CURRENT) - Integrated `useWhitelabelContext` to dynamically hide columns
 *                                   and customize column headers based on whitelabel configuration.
 *
 * -----
 * SECURITY_RISK: LOW - This component displays read-only cost information for products.
 *                      It does not handle sensitive data or perform write operations.
 * INTENT: Render a paginated table of product costs, including detailed complex cost structures
 *         and tiered pricing, with an option to export data, now with whitelabeling capabilities.
 * PROMPTER_INSTRUCTIONS: When modifying, ensure cost formatting is consistent and
 *                        that complex cost details are presented clearly.
 * LLM_CONFIDENCE: 100% - Standard table component with pagination and display of complex data.
 */
import React, { useState } from 'react';
import { CostProduct, ComplexCost } from '../types/costwand.d';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table.tsx';
import Pagination from './ui/Pagination.tsx';
import { Link } from 'react-router-dom';
import { Button } from './ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog.tsx';
import { Spinner } from './ui/spinner.tsx';
import { apiClient } from '../api/apiClient.tsx';
import { useWhitelabelContext } from '../context/WhitelabelContext.tsx'; // NEW: Import useWhitelabelContext

// New component to display tiered cost details
interface TieredCostViewerProps {
  tieredCosts: ComplexCost['tiered'];
  entityId: string; // NEW: Add entityId prop
}

const TieredCostViewer: React.FC<TieredCostViewerProps> = ({ tieredCosts, entityId = 'costs' }) => { // NEW: Default entityId
  const { whitelabelConfig } = useWhitelabelContext(); // NEW: Use whitelabel context

  const getFieldTitle = (fieldId: string, defaultTitle: string) =>
    whitelabelConfig.entities?.[entityId]?.fields?.[fieldId]?.title || defaultTitle;

  if (!tieredCosts || Object.keys(tieredCosts).length === 0) {
    return <p className="text-muted-foreground">No tiered costs available.</p>;
  }
  return (
    <ul className="list-disc list-inside space-y-1">
      {Object.entries(tieredCosts).map(([quantity, tierData]) => (
        <li key={quantity}>
          <span className="font-semibold">{quantity} {getFieldTitle('units', 'units')}:</span>
          <br />{getFieldTitle('unit_price', 'Unit Price')}: ${tierData.unit_price.toFixed(2)}
          <br />{getFieldTitle('discount', 'Discount')}: {tierData.discount_perc.toFixed(2)}%
          <br />{getFieldTitle('total_value', 'Total Value')}: ${tierData.tier_total_value.toFixed(2)}
        </li>
      ))}
    </ul>
  );
};


interface CostTableProps {
  costs: CostProduct[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  loading: boolean;
  error: string | null;
  baseEndpoint: string;
  entityId: string; // NEW: Add entityId prop
}

const CostTable: React.FC<CostTableProps> = ({
  costs,
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
  loading,
  error,
  baseEndpoint,
  entityId, // NEW: Destructure entityId
}) => {
  const { whitelabelConfig } = useWhitelabelContext(); // NEW: Use whitelabel context
  const [exportLoading, setExportLoading] = useState(false);

  const getFieldConfig = (fieldId: string) =>
    whitelabelConfig.entities?.[entityId]?.fields?.[fieldId];

  const isFieldVisible = (fieldId: string) =>
    getFieldConfig(fieldId)?.visible !== false; // Default to visible

  const getFieldTitle = (fieldId: string, defaultTitle: string) =>
    getFieldConfig(fieldId)?.title || defaultTitle;

  const handleExportCsv = async () => {
    setExportLoading(true);
    try {
      await apiClient<void>(baseEndpoint, { downloadCsv: true });
    } catch (err: unknown) {
      // Error already handled by apiClient and globalErrorHandler
    } finally {
      setExportLoading(false);
    }
  };

  if (loading)
    return (
      <p className="text-center text-muted-foreground flex items-center justify-center gap-2"><Spinner size="sm" /> Loading Costs...</p>
    );

    if (costs.length === 0 && !loading)
    return (
      <p className="text-center text-muted-foreground">No Costs found.</p>
    );

  // Helper to format cost, handling both number and ComplexCost
  const formatCost = (cost: CostProduct['cost']): React.ReactNode => {
    if (typeof cost === 'number') {
      return '$' + cost.toFixed(2);
    } else if (cost && typeof cost === 'object' && 'base' in cost) {
      return (
        <div>
          <p>{getFieldTitle('base_cogs', 'Base COGS')}: ${cost.base.cogs_total?.toFixed(2) ?? 'N/A'}</p>
          {cost.margin && (
            <p className="text-xs text-muted-foreground">
              ({getFieldTitle('markup', 'Markup')}: {cost.margin.mark_up_perc}%, {getFieldTitle('selling_base', 'Selling Base')}: ${cost.margin.selling_price_base?.toFixed(2)})
            </p>
          )}
          {cost.tiered && Object.keys(cost.tiered).length > 0 && (
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="default">
                  {getFieldTitle('view_tiered_costs', 'View Tiered Costs')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{getFieldTitle('tiered_costs_dialog_title', 'Tiered Costs')}</DialogTitle>
                </DialogHeader>
                <TieredCostViewer tieredCosts={cost.tiered} entityId={entityId} />
              </DialogContent>
            </Dialog>
          )}
        </div>
      );
    }
    return 'N/A';
  };

  return (
    <div className="rounded-md border">
      <div className="flex justify-end mb-4">
        <Button onClick={handleExportCsv} disabled={loading || exportLoading} variant="outline">
          {exportLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Exporting...</span> : 'Export to CSV'}
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            {isFieldVisible('description') && <TableHead>{getFieldTitle('description', 'Description')}</TableHead>}
            {isFieldVisible('article_number') && <TableHead>{getFieldTitle('article_number', 'Article No.')}</TableHead>}
            {isFieldVisible('vendor_id') && <TableHead>{getFieldTitle('vendor_id', 'Vendor')}</TableHead>}
            {isFieldVisible('family_id') && <TableHead>{getFieldTitle('family_id', 'Family')}</TableHead>}
            {isFieldVisible('cost') && <TableHead>{getFieldTitle('cost', 'Cost')}</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {costs.map((cost) => (
            <TableRow key={cost.id}>
              {isFieldVisible('description') && <TableCell>{cost.description}</TableCell>}
              {isFieldVisible('article_number') && <TableCell>{cost.article_number}</TableCell>}
              {isFieldVisible('vendor_id') && (
                <TableCell>
                  {cost._labels?.vendor_id || `ID: ${cost.vendor_id}`}
                </TableCell>
              )}
              {isFieldVisible('family_id') && (
                <TableCell>
                  {cost._labels?.family_id || `ID: ${cost.family_id}`}
                </TableCell>
              )}
              {isFieldVisible('cost') && (
                <TableCell>
                  {formatCost(cost.cost)}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={onPageChange}
        disabled={loading || exportLoading}
      />
    </div>
  );
};

export default CostTable;